/**
 * 「离开房间」这一下真正做的事。顶栏的两段式「离开 → 确认离开」（旁观者 / 没有行动权的页）
 * 和「我的座位」面板里的「离开房间」走同一条路 —— 以前它写死在 RoomHeader 里，
 * 面板里再抄一份，两边迟早漂：影子页那条换连接的路漏抄一次，那条 WS 就永远绑在房间上。
 *
 * 二次确认不归这里管：顶栏靠「确认离开」，面板靠「先打开面板、再点离开房间」。
 */
import { useCallback } from 'react'
import type { PlayerView } from '@poker/shared'

import { restartOnlineSession } from '../app/session.js'
import { useUiStore } from '../store/index.js'
import { selectIsActor, useNetStore } from '../transport/applyMessage.js'

import { leaveRoomMode } from './roomDerive.js'
import { useRoomLink } from './roomLink.js'

/**
 * @param onLeave 离开之后的去向（一般是「回大厅」）
 * @param view    判「快照到了没」用的视图；RoomHeader 的单测会用 prop 覆盖它，所以由调用方传
 */
export function useLeaveRoom(onLeave: (() => void) | undefined, view: PlayerView | null): () => void {
  const isActor = useNetStore(selectIsActor)
  const link = useRoomLink()
  const showToast = useUiStore((s) => s.showToast)

  return useCallback((): void => {
    if (leaveRoomMode(isActor, view !== null) === 'send') {
      link.send({ type: 'leaveRoom' })
      onLeave?.()
    } else {
      // 这一页没有行动权：只关本窗口，座位留给正在操作的那一页
      showToast('已回到大厅，你的座位保留在另一个窗口', 'info')
      onLeave?.()
      /**
       * ★★ 必须把这条 WS 换掉，否则它在服务端**永远绑着这个房间**。
       *
       * 服务端只在三处解绑：房间没了、actor 停了、`leaveRoom` 被接受。
       * 影子页这条路一处都碰不到 —— 于是 `session.roomId` 一直非空，
       * 网关的红线 #39 校验对 createRoom / joinRoom 一律回 E_ALREADY_IN_ROOM，
       * **这条连接从此再也进不了任何房间**，只能刷新整页才能恢复。
       *
       * 用 restartOnlineSession 而不是自己 close：它先同步清掉 session/inFlight
       * 再 await 新 boot，是邮箱登录一直在走的那条成熟路径。
       * 旧连接断开只从 member 里摘掉**这一个 session**，另一台设备照常在桌上。
       */
      void restartOnlineSession()
    }
  }, [isActor, link, onLeave, showToast, view])
}
